import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { WebService } from './web.service';

export type FilterDefinition = {
  FilterType: string;
  value: any;
};

export type FilterMap = Record<string, FilterDefinition>;

export type FilterState = {
  tableId: string;
  filters: FilterMap;
};

export type FilterPreset = {
  FilterPresetID?: number;
  FilterPresetName?: string;
  DefaultFilter?: boolean;
  SystemCreated?: boolean;
  DefaultedBy?: string;
  UserName?: string | null;
  FilterString?: string;
};

export type SaveFilterPresetRequest = FilterPreset & {
  tableId: string;
  filters: FilterMap;
};

@Injectable({ providedIn: 'root' })
export class FilterService {
  private readonly filtersSubject = new BehaviorSubject<FilterState | null>(null);
  private readonly filtersByTable = new Map<string, FilterMap>();
  private readonly initialLoads = new Map<string, Promise<FilterMap>>();

  readonly filters$ = this.filtersSubject.asObservable();

  constructor(private webService: WebService) { }

  updateFilters(tableId: string, filters: FilterMap): void {
    const normalizedTableId = tableId || 'rms-table';
    const clonedFilters = this.cloneFilters(filters);
    const key = this.stateKey(tableId);
    this.filtersByTable.set(key, clonedFilters);
    try { sessionStorage.setItem(key, JSON.stringify(clonedFilters)); } catch { /* Memory still preserves navigation state. */ }
    this.filtersSubject.next({
      tableId: normalizedTableId,
      filters: clonedFilters
    });
  }

  getCurrentFilters(tableId: string): FilterMap {
    this.hasCurrentFilters(tableId);
    return this.cloneFilters(this.filtersByTable.get(this.stateKey(tableId)) ?? {});
  }

  private stateKey(tableId: string): string {
    return `rms-filters:${this.getCurrentUserName().toLowerCase()}:${tableId || 'rms-table'}`;
  }

  hasCurrentFilters(tableId: string): boolean {
    const key = this.stateKey(tableId);
    if (!this.filtersByTable.has(key)) {
      try {
        const saved = sessionStorage.getItem(key);
        if (saved !== null) this.filtersByTable.set(key, this.parsePresetFilters(saved));
      } catch { /* Ignore unavailable storage or an invalid old value. */ }
    }
    return this.filtersByTable.has(key);
  }

  async initializeFilters(tableId: string): Promise<FilterMap> {
    if (this.hasCurrentFilters(tableId)) return this.getCurrentFilters(tableId);
    const key = this.stateKey(tableId);
    let pending = this.initialLoads.get(key);
    if (!pending) {
      pending = (async () => {
        const defaults = await this.getDefaultFilter(tableId);
        // A user may edit or clear filters while the default request is in flight.
        if (!this.hasCurrentFilters(tableId)) this.updateFilters(tableId, defaults ?? {});
        return this.getCurrentFilters(tableId);
      })();
      this.initialLoads.set(key, pending);
    }
    try { return await pending; }
    finally { this.initialLoads.delete(key); }
  }

  async deletePreset(preset: FilterPreset): Promise<void> {
    if (!preset.FilterPresetID || preset.SystemCreated ||
        preset.UserName?.toLowerCase() !== this.getCurrentUserName().toLowerCase()) {
      throw new Error('Only your own filters can be deleted.');
    }
    await lastValueFrom(this.webService.getWebCall('users', '/filters/delete', {
      body: { FilterPresetID: preset.FilterPresetID, UserName: this.getCurrentUserName() }
    }));
  }
  async getDefaultFilter(
    tableId: string
  ): Promise<FilterMap | null> {
    const presets = await this.getPresetFilters(tableId);

    const currentUser =
      this.getCurrentUserName().toLowerCase();

    const defaultFilter =
      presets.find(p =>
        p.DefaultFilter ||
        (
          p.DefaultedBy &&
          p.DefaultedBy
            .split(',')
            .map(x => x.trim().toLowerCase())
            .includes(currentUser)
        )
      );

    if (!defaultFilter?.FilterString)
      return null;

    return this.parsePresetFilters(
      defaultFilter.FilterString
    );
  }
  clearFilters(tableId: string): void {
    this.updateFilters(tableId, {});
  }

  async saveFilters(request: SaveFilterPresetRequest): Promise<any> {
    const filterPayload = {
      FilterPresetID: request.FilterPresetID,
      FilterPresetName: request.FilterPresetName,
      DefaultFilter: request.DefaultFilter ?? false,
      UserName: request.UserName ?? this.getCurrentUserName(),
      SystemCreated: request.SystemCreated ?? false,
      DefaultedBy: request.DefaultedBy ?? '',
      FilterString: JSON.stringify(this.cloneFilters(request.filters))
    };

    console.log('Filters saved:', filterPayload);

    return lastValueFrom(
      this.webService.getWebCall('users', '/filters/merge', {
        body: filterPayload
      })
    );
  }

  async getPresetFilters(tableId?: string): Promise<FilterPreset[]> {
    const params: Array<{ value: string; name: string }> = [
      { value: this.getCurrentUserName(), name: 'UserName' }
    ];

    // This is included only when the API supports filter presets per table.
    // Remove it if the existing endpoint accepts UserName only.
    if (tableId) {
      params.push({ value: tableId, name: 'TableID' });
    }

    return lastValueFrom(
      this.webService.getWebCall('users', '/filters', { params })
    );
  }

  parsePresetFilters(filterString: string): FilterMap {
    const parsed = JSON.parse(filterString || '{}');

    // Supports both the current saved shape and older wrapped presets.
    const filters = parsed?.filters && typeof parsed.filters === 'object'
      ? parsed.filters
      : parsed;

    if (!filters || Array.isArray(filters) || typeof filters !== 'object') {
      return {};
    }

    return this.cloneFilters(filters as FilterMap);
  }

  getCurrentUserName(): string {
    const email = localStorage.getItem('email') ?? '';
    return email.includes('@') ? email.split('@')[0] : email;
  }

  private cloneFilters(filters: FilterMap): FilterMap {
    return Object.fromEntries(
      Object.entries(filters ?? {}).map(([key, filter]) => [
        key,
        {
          ...filter,
          value: filter?.value && typeof filter.value === 'object'
            ? { ...filter.value }
            : filter?.value
        }
      ])
    );
  }
}
